/**
 * The conversation list, loaded once and shared.
 *
 * Keeping one subscription here rather than one per screen matters: on
 * Firebase every listener is billed reads, so duplicating this across screens
 * would multiply the bill for no benefit.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { chats } from '@zivapp/shared'
import { useAuth } from './AuthContext.jsx'

const ChatContext = createContext(null)

export function ChatProvider({ children }) {
  const { isSignedIn } = useAuth()
  const [chatList, setChatList] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isSignedIn) {
      setChatList([])
      setIsLoading(false)
      return undefined
    }
    setIsLoading(true)
    return chats.subscribeToList(list => {
      setChatList(list)
      setIsLoading(false)
    })
  }, [isSignedIn])

  const value = useMemo(() => {
    const visible = chatList.filter(chat => !chat.isArchived)
    return {
      chats: visible,
      archived: chatList.filter(chat => chat.isArchived),
      isLoading,
      totalUnread: visible.reduce((sum, chat) => sum + chat.unreadCount, 0),
      openDirect: chats.getOrCreateDirect,
      createGroup: chats.createGroup
    }
  }, [chatList, isLoading])

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export function useChats() {
  const context = useContext(ChatContext)
  if (!context) throw new Error('useChats must be used inside ChatProvider')
  return context
}
